import React, { useState } from 'react';
import { Layers, BarChart, Info, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации расчета налога
 */
const TaxBreakdown = ({ taxDetails, results, taxObjectType }) => {
  const [activeTab, setActiveTab] = useState('breakdown');
  
  if (!taxDetails || taxDetails.length === 0 || !results) {
    return null;
  }

  // Группировка деталей по типу влияния на налог (положительное или отрицательное)
  const positiveImpacts = taxDetails.filter(item => !item.impact || item.impact >= 0);
  const negativeImpacts = taxDetails.filter(item => item.impact < 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация расчета налога
        </h3>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('breakdown')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'breakdown'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Детализация расчета
        </button>
        <button
          onClick={() => setActiveTab('factors')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'factors'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Влияющие факторы
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>
            {activeTab === 'breakdown' 
              ? "Подробная информация о каждом параметре, влияющем на расчет налога, и его вкладе в общую сумму." 
              : "Анализ факторов, увеличивающих и уменьшающих налоговую нагрузку."}
          </p>
        </div>
      </div>

      {activeTab === 'breakdown' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 tax-details-table">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Параметр</th>
                <th scope="col" className="px-4 py-3 font-semibold">Значение</th>
                <th scope="col" className="px-4 py-3 font-semibold">Коэффициент</th>
                <th scope="col" className="px-4 py-3 font-semibold text-right">Влияние на налог</th>
                <th scope="col" className="px-4 py-3 font-semibold">Описание</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {taxDetails.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3">{item.value}</td>
                  <td className="px-4 py-3">{item.coefficient !== null ? formatNumber(item.coefficient, 2) : '-'}</td>
                  <td className={`px-4 py-3 text-right font-medium ${
                    item.impact > 0 
                      ? 'text-red-600 dark:text-red-400' 
                      : item.impact < 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {item.impact !== null 
                      ? (item.impact >= 0 
                          ? `+${formatCurrency(item.impact, 0)}` 
                          : formatCurrency(item.impact, 0))
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{item.description}</td>
                </tr>
              ))}
              
              {/* Итоговая строка */}
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-4 py-3" colSpan="3">Итоговая сумма налога:</td>
                <td className="px-4 py-3 text-right">{formatCurrency(results.totalTax, 0)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'factors' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Факторы, увеличивающие налог */}
          <div className="rounded-lg border border-red-100 dark:border-red-800 overflow-hidden">
            <div className="bg-red-50 dark:bg-red-900/20 px-4 py-3">
              <h4 className="font-medium text-red-700 dark:text-red-300 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Факторы, увеличивающие налог
              </h4>
            </div>
            <div className="p-4">
              {positiveImpacts.length > 0 ? (
                <ul className="space-y-2">
                  {positiveImpacts.map((item, index) => (
                    <li key={index} className="flex justify-between items-start pb-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <span className="text-sm">{item.name}</span>
                      <span className={`text-sm font-medium ${
                        item.impact > 0 
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {item.impact !== null 
                          ? (item.impact > 0 ? `+${formatCurrency(item.impact, 0)}` : '-')
                          : formatCurrency(results.baseTax, 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Нет увеличивающих факторов</p>
              )}
            </div>
          </div>
          
          {/* Факторы, уменьшающие налог */}
          <div className="rounded-lg border border-green-100 dark:border-green-800 overflow-hidden">
            <div className="bg-green-50 dark:bg-green-900/20 px-4 py-3">
              <h4 className="font-medium text-green-700 dark:text-green-300 flex items-center">
                <TrendingDown className="w-4 h-4 mr-2" />
                Факторы, уменьшающие налог
              </h4>
            </div>
            <div className="p-4">
              {negativeImpacts.length > 0 ? (
                <ul className="space-y-2">
                  {negativeImpacts.map((item, index) => (
                    <li key={index} className="flex justify-between items-start pb-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <span className="text-sm">{item.name}</span>
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">
                        {formatCurrency(item.impact, 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Нет уменьшающих факторов</p>
              )}
            </div>
          </div>
          
          {/* Анализ оптимизации */}
          <div className="col-span-full bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800 mt-2">
            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Возможности для оптимизации налога:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-blue-600 dark:text-blue-400">
              {taxObjectType === 'car' ? (
                <>
                  <li>Выбор автомобиля стоимостью менее 3 млн рублей для избежания повышающего коэффициента</li>
                  <li>Регистрация на льготника (при наличии в семье)</li>
                  <li>Покупка электромобиля (льгота в ряде регионов)</li>
                  <li>Выбор автомобиля мощностью менее 100-150 л.с. (более низкие базовые ставки)</li>
                </>
              ) : (
                <>
                  <li>Оформление налоговых вычетов на единственное жилье</li>
                  <li>Оформление долей на детей или льготников в семье</li>
                  <li>Выбор региона с более низкими ставками налога на имущество</li>
                  <li>Своевременное обжалование завышенной кадастровой стоимости</li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxBreakdown;